import { useState } from 'react';
import type { FieldErrors, UseFormHandleSubmit, UseFormRegister } from 'react-hook-form';
import { Eye, EyeOff, Mail, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { OneLoginIcon } from '@/shared/components/ui/onelogin-icon';
import { ReneoLogo as SharedReneoLogo } from '@/shared/components/ReneoLogo';
import type { LoginFormValues } from '../../model/auth.schema';

export type ViewState = 'LOGIN' | 'EMAIL_LOGIN' | 'PASSWORD_RESET';

interface AuthorizationFormPanelProps {
    view: ViewState;
    error: string | null;
    isLoading: boolean;
    showEmailPassword: boolean;
    passwordResetEmail: string;
    isLoadingPasswordReset: boolean;
    passwordResetSent: boolean;
    cooldownRemaining: number;
    errors: FieldErrors<LoginFormValues>;
    register: UseFormRegister<LoginFormValues>;
    handleSubmit: UseFormHandleSubmit<LoginFormValues>;
    onDismissError: () => void;
    onOneLogin: () => void;
    onShowEmailLogin: () => void;
    onBackToLogin: () => void;
    onEmailLogin: (data: LoginFormValues) => Promise<void>;
    onShowPasswordReset: () => void;
    onPasswordResetEmailChange: (value: string) => void;
    onCancelPasswordReset: () => void;
    onSendPasswordReset: () => void;
    isResetDisabled: boolean;
}

export function AuthorizationFormPanel({
    view,
    error,
    isLoading,
    showEmailPassword,
    passwordResetEmail,
    isLoadingPasswordReset,
    passwordResetSent,
    cooldownRemaining,
    errors,
    register,
    handleSubmit,
    onDismissError,
    onOneLogin,
    onShowEmailLogin,
    onBackToLogin,
    onEmailLogin,
    onShowPasswordReset,
    onPasswordResetEmailChange,
    onCancelPasswordReset,
    onSendPasswordReset,
    isResetDisabled,
}: AuthorizationFormPanelProps) {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    return (
        <div className="mx-auto w-full max-w-sm">
            <div className="mb-16">
                <SharedReneoLogo size="md" variant="icon" className="h-auto w-[170px]" />
            </div>

            <h2 className="mb-8 text-3xl font-semibold tracking-tight text-white">{view === 'PASSWORD_RESET' ? 'Forgot password?' : 'Sign in'}</h2>

            {error ? (
                <Alert variant="destructive" className="mb-6 border border-red-300/40 bg-red-100/95 pr-10 text-red-900">
                    <button
                        type="button"
                        className="absolute right-3 top-3 rounded-sm p-1 text-destructive/70 transition hover:text-destructive"
                        aria-label="Dismiss error"
                        onClick={onDismissError}
                    >
                        <X className="h-4 w-4" />
                    </button>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            ) : null}

            {view === 'LOGIN' ? (
                <>
                    <div className="flex flex-col gap-4">
                        <Button
                            className="w-full justify-center gap-3 bg-[#00515D] text-white hover:bg-[#0B6070]"
                            iconColor="inherit"
                            size="lg"
                            type="button"
                            variant="primary"
                            disabled={isLoading}
                            dataTestId="ram-auth-login-onelogin"
                            onClick={onOneLogin}
                        >
                            <OneLoginIcon className="h-4 w-4" />
                            {isLoading ? 'Signing in...' : 'Enter via OneLogin'}
                        </Button>

                        {showEmailPassword ? (
                            <Button
                                className="w-full justify-center gap-3 border-[#0B8E9D] bg-transparent text-[#0B8E9D] hover:bg-[#00515D]/18 hover:text-[#3FC9D5]"
                                size="lg"
                                type="button"
                                variant="outline"
                                disabled={isLoading}
                                dataTestId="ram-auth-login-email-option"
                                onClick={onShowEmailLogin}
                            >
                                <Mail className="h-4 w-4" />
                                Email and password
                            </Button>
                        ) : null}
                    </div>

                    {showEmailPassword ? <p className="mt-4 text-sm italic text-[#7F99AD]">(External users only)</p> : null}
                </>
            ) : null}

            {showEmailPassword && view === 'EMAIL_LOGIN' ? (
                <form className="space-y-4" onSubmit={handleSubmit(onEmailLogin)}>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label className="mb-0 text-white" htmlFor="email">
                                Email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="Enter your email"
                                className="border-white/20 bg-white/95 text-[#01233C] placeholder:text-slate-400"
                                {...register('email')}
                                aria-invalid={!!errors.email}
                                size="lg"
                                dataTestId="ram-auth-login-email"
                            />
                            {errors.email ? <p className="text-sm text-red-500">{errors.email.message}</p> : null}
                        </div>

                        <div className="space-y-2">
                            <Label className="mb-0 text-white" htmlFor="password">
                                Password
                            </Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={isPasswordVisible ? 'text' : 'password'}
                                    placeholder="Enter your password"
                                    className="border-white/20 bg-white/95 pr-11 text-[#01233C] placeholder:text-slate-400"
                                    {...register('password')}
                                    aria-invalid={!!errors.password}
                                    size="lg"
                                    dataTestId="ram-auth-login-password"
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-700"
                                    aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                                    data-testid="ram-auth-login-toggle-password-visibility"
                                    onClick={() => setIsPasswordVisible(current => !current)}
                                >
                                    {isPasswordVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.password ? <p className="text-sm text-red-500">{errors.password.message}</p> : null}
                            <button
                                type="button"
                                className="block text-right text-sm text-[#93D9DF] hover:underline"
                                data-testid="ram-auth-login-forgot-password"
                                onClick={onShowPasswordReset}
                            >
                                Forgot password?
                            </button>
                        </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <Button
                            className="flex-1 border-white/20 bg-white/10 text-white hover:bg-white/20"
                            type="button"
                            variant="secondary"
                            size="lg"
                            dataTestId="ram-auth-login-back-main"
                            onClick={onBackToLogin}
                        >
                            Go back
                        </Button>
                        <Button
                            className="flex-1 bg-[#00515D] text-white hover:bg-[#0B6070]"
                            type="submit"
                            variant="primary"
                            size="lg"
                            disabled={isLoading}
                            dataTestId="ram-auth-login-submit"
                        >
                            {isLoading ? 'Signing in...' : 'Sign in'}
                        </Button>
                    </div>
                </form>
            ) : null}

            {view === 'PASSWORD_RESET' ? (
                !passwordResetSent ? (
                    <div className="space-y-4">
                        <p className="mb-4 text-[#A6C4CE]">Enter your email and we&apos;ll send you a link to reset your password.</p>

                        <div className="space-y-2">
                            <Label className="mb-0 text-white" htmlFor="reset-email">
                                Email
                            </Label>
                            <Input
                                id="reset-email"
                                type="email"
                                placeholder="Enter your email"
                                className="border-white/20 bg-white/95 text-[#01233C] placeholder:text-slate-400"
                                value={passwordResetEmail}
                                onChange={event => onPasswordResetEmailChange(event.target.value)}
                                size="lg"
                                dataTestId="ram-auth-password-reset-email"
                            />
                        </div>

                        <div className="flex gap-3 pt-2">
                            <Button
                                className="flex-1 border-white/20 bg-white/10 text-white hover:bg-white/20"
                                type="button"
                                variant="secondary"
                                size="lg"
                                dataTestId="ram-auth-password-reset-cancel"
                                onClick={onCancelPasswordReset}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="flex-1 bg-[#00515D] text-white hover:bg-[#0B6070]"
                                type="button"
                                variant="primary"
                                size="lg"
                                disabled={isResetDisabled}
                                dataTestId="ram-auth-password-reset-send"
                                onClick={onSendPasswordReset}
                            >
                                {cooldownRemaining > 0 ? `Send reset link (${cooldownRemaining}s)` : isLoadingPasswordReset ? 'Sending...' : 'Send reset link'}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <p className="mb-4 text-[#93D9DF]">Reset link sent. Check your email.</p>
                        <Button
                            className="w-full bg-[#00515D] text-white hover:bg-[#0B6070]"
                            type="button"
                            variant="primary"
                            size="lg"
                            dataTestId="ram-auth-password-reset-back-login"
                            onClick={onCancelPasswordReset}
                        >
                            Back to login
                        </Button>
                    </div>
                )
            ) : null}
        </div>
    );
}
